/**
 * One floating card per ProseMirror editor (#250): Harper fix menus and
 * entity peek cards share it. Positioned with Floating UI against a document
 * range, `fixed` on `document.body` so the editor's scroller can't clip it.
 */

import {
  autoUpdate,
  computePosition,
  flip,
  hide,
  limitShift,
  offset,
  shift,
  type VirtualElement,
} from "@floating-ui/dom";
import type { EditorView } from "prosemirror-view";

export type PopoverAnchor = { from: number; to: number };

export type ProsePopover = {
  /** Replace whatever is showing with `content`, anchored to `anchor`. */
  show(
    anchor: PopoverAnchor,
    content: HTMLElement,
    options: { above: boolean; className: string },
  ): void;
  hide(): void;
  isOpen(): boolean;
  /** The range the open card points at. */
  anchor(): PopoverAnchor | null;
  /** True when `node` is inside the card. */
  contains(node: Node | null): boolean;
  readonly dom: HTMLElement;
  destroy(): void;
};

/** Box around the first line of `from..to` (the whole range when it fits on one line). */
function rangeRect(view: EditorView, anchor: PopoverAnchor): DOMRect {
  const size = view.state.doc.content.size;
  const from = Math.max(0, Math.min(anchor.from, size));
  const to = Math.max(from, Math.min(anchor.to, size));
  const start = view.coordsAtPos(from, 1);
  const end = view.coordsAtPos(to, -1);
  const sameLine = Math.abs(end.top - start.top) < 2 && end.right >= start.left;
  const right = sameLine ? end.right : start.left;
  return new DOMRect(
    start.left,
    start.top,
    right - start.left,
    start.bottom - start.top,
  );
}

/** `getView` is read lazily, so the popover can exist before its view. */
export function createProsePopover(getView: () => EditorView): ProsePopover {
  const dom = document.createElement("div");
  dom.className = "zw-prose-popover";
  dom.hidden = true;
  document.body.appendChild(dom);

  let current: PopoverAnchor | null = null;
  let stopTracking: (() => void) | null = null;

  function hidePopover(): void {
    stopTracking?.();
    stopTracking = null;
    current = null;
    dom.hidden = true;
    dom.replaceChildren();
  }

  return {
    dom,
    show(anchor, content, options) {
      hidePopover();
      const view = getView();
      const reference: VirtualElement = {
        getBoundingClientRect: () =>
          current === null ? new DOMRect() : rangeRect(view, current),
        contextElement: view.dom,
      };
      current = anchor;
      dom.className = `zw-prose-popover ${options.className}`;
      dom.replaceChildren(content);
      dom.hidden = false;
      dom.style.visibility = "hidden";
      const placement = options.above ? "top-start" : "bottom-start";
      const place = async (): Promise<void> => {
        if (current === null || view.dom.isConnected === false) return;
        const result = await computePosition(reference, dom, {
          strategy: "fixed",
          placement,
          middleware: [
            offset(4),
            flip({ padding: 8 }),
            shift({ padding: 8, limiter: limitShift() }),
            hide({ padding: 8 }),
          ],
        });
        if (current === null) return;
        dom.style.left = `${Math.round(result.x)}px`;
        dom.style.top = `${Math.round(result.y)}px`;
        // Close once the anchor scrolls out of view.
        if (result.middlewareData.hide?.referenceHidden === true) {
          hidePopover();
          return;
        }
        dom.style.visibility = "";
      };
      stopTracking = autoUpdate(reference, dom, () => void place());
    },
    hide: hidePopover,
    isOpen: () => current !== null,
    anchor: () => current,
    contains: (node) => node !== null && dom.contains(node),
    destroy() {
      hidePopover();
      dom.remove();
    },
  };
}
